import { useReducer, useRef, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ToastCustom, { IToastRef } from "component/Toast/Toast";
import ButtonBox from "component/ButtonBox/ButtonBox";
import DialogBox from "component/Dialog/Dialog";
import pageUploadPDFReducer, {
  INITIAL_PAGE_UPLOAD_PDF_STATE,
} from "../../reducer/PageUploadPDFReducer";
import PageUploadAudioController from "../../controller/PageUploadAudioController";
import PageUploadAudioAPIService from "../../service/PageUploadAudioAPIService";
import { linkAuthRoute } from "routes";
import { deriveWorkflowStatus, type StatusStepsMap } from "./useAudioWorkflowStatus";
import WorkflowsListTable, { type WorkflowListRow } from "./WorkflowsListTable";
import WorkflowStepsView from "./WorkflowStepsView";
import UploadFormView from "./UploadFormView";

interface UploadAudioResponseData {
  status?: string;
  message?: string;
  fileName?: string;
  url?: string;
  contentType?: string;
  size?: number;
  isPrivate?: boolean;
  workflowTriggered?: boolean;
}

/** Query param carrying the audio url of a triggered workflow to track in the dialog. */
const AUDIO_URL_PARAM = "audio";
/** Accepted alias for the same param (advertised by some entry points). */
const AUDIO_URL_ALIAS_PARAM = "audio-url";

type WorkflowFilter = "all" | "completed" | "processing" | "error";

const FILTERS: Array<{ key: WorkflowFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "completed", label: "Completed" },
  { key: "processing", label: "Processing" },
  { key: "error", label: "Failed" },
];

/** Parse a query param from the live URL (fallback for host/remote setups where the router may not see it). */
const readQueryParam = (name: string): string => {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get(name) ?? "";
};

/** Best-effort display name for a workflow row from its audio file url. */
const fileNameFromUrl = (fileUrl: string): string => {
  if (!fileUrl) return "Untitled audio";
  try {
    const path = new URL(fileUrl, window.location.origin).pathname;
    return decodeURIComponent(path.split("/").filter(Boolean).at(-1) ?? fileUrl);
  } catch {
    return fileUrl.split("/").filter(Boolean).at(-1) ?? fileUrl;
  }
};

const PageUploadAudio = () => {
  const [state, dispatch] = useReducer(pageUploadPDFReducer, INITIAL_PAGE_UPLOAD_PDF_STATE);
  // Keep one controller instance across renders so its polling timers survive;
  // refresh its state reference every render so reads are never stale.
  const controllerRef = useRef<PageUploadAudioController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new PageUploadAudioController(state, dispatch);
  } else {
    (controllerRef.current as any).state = state;
  }
  const controller = controllerRef.current;

  const toastMessageRef = useRef<IToastRef | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // An `audio` (or `audio-url`) query param means a workflow is being tracked: the
  // steps dialog is open and long-polling `/audio/progress`. The URL is the single
  // source of truth, so row clicks, uploads and reloads all share the same path.
  const audioUrlParam =
    searchParams.get(AUDIO_URL_PARAM) ||
    searchParams.get(AUDIO_URL_ALIAS_PARAM) ||
    readQueryParam(AUDIO_URL_PARAM) ||
    readQueryParam(AUDIO_URL_ALIAS_PARAM);

  const [filter, setFilter] = useState<WorkflowFilter>("all");
  const [processingStartMessage, setProcessingStartMessage] = useState<string>("");

  // Which view the dialog shows: a tracked workflow's steps, the multi-upload job
  // logs, the upload form — or nothing.
  const dialogMode: "closed" | "form" | "steps" | "job" = audioUrlParam
    ? "steps"
    : state.data.fileUploadUUID
      ? "job"
      : state.data.showUploadModal
        ? "form"
        : "closed";

  // Fetch the workflows list and keep it fresh (10s poll with backoff) while the
  // page is mounted. The list stays live behind the dialog.
  useEffect(() => {
    controller.fetchWorkflows();
    controller.startWorkflowsListPolling();
    return () => {
      controller.stopWorkflowsListPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for job completion when a multi-file upload job is running.
  useEffect(() => {
    if (!state.data.fileUploadUUID || state.data.isProcessCompleted) {
      return;
    }

    controller.startJobPolling((jobResult) => {
      if (jobResult && jobResult.status === "completed") {
        toastMessageRef.current?.showFunction?.({
          message: "Processing and analysis completed successfully!",
          severity: "success",
        });
      } else if (jobResult && jobResult.status === "error") {
        toastMessageRef.current?.showFunction?.({
          message: "Error processing audio file. Please try again.",
          severity: "error",
        });
      }
    });

    return () => {
      controller.stopJobPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data.fileUploadUUID, state.data.isProcessCompleted]);

  // Rows for the landing list: overall status derived with the same logic the
  // steps dialog uses (deriveWorkflowStatus), so list and popup always agree.
  const workflowRows = useMemo<WorkflowListRow[]>(
    () =>
      state.data.audioWorkflows.map((workflow) => {
        const summary = deriveWorkflowStatus((workflow.steps as StatusStepsMap) || {});
        return {
          id: workflow.id,
          fileUrl: workflow.fileUrl,
          fileName: fileNameFromUrl(workflow.fileUrl),
          status: summary.status,
          completedCount: summary.completedCount,
          updatedAt: workflow.updatedAt || workflow.createdAt,
        };
      }),
    [state.data.audioWorkflows]
  );

  // Client-side filter of the current page (the endpoint has no status filter).
  const filteredRows = useMemo(() => {
    switch (filter) {
      case "completed":
        return workflowRows.filter((row) => row.status === "succeeded");
      case "processing":
        return workflowRows.filter(
          (row) => row.status === "running" || row.status === "pending"
        );
      case "error":
        return workflowRows.filter((row) => row.status === "failed");
      default:
        return workflowRows;
    }
  }, [workflowRows, filter]);

  const sortedProcessingLogs = useMemo(
    () =>
      [...state.data.processingLogs].sort(
        (a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)
      ),
    [state.data.processingLogs]
  );

  const setAudioUrlParam = (url: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(AUDIO_URL_PARAM, url);
    next.delete(AUDIO_URL_ALIAS_PARAM);
    setSearchParams(next, { replace: true });
  };

  const clearAudioUrlParam = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(AUDIO_URL_PARAM);
    next.delete(AUDIO_URL_ALIAS_PARAM);
    setSearchParams(next, { replace: true });
    // Host/remote fallback: when the router doesn't own these params, strip them
    // from the live URL too so the dialog actually closes.
    if (readQueryParam(AUDIO_URL_PARAM) || readQueryParam(AUDIO_URL_ALIAS_PARAM)) {
      const url = new URL(window.location.href);
      url.searchParams.delete(AUDIO_URL_PARAM);
      url.searchParams.delete(AUDIO_URL_ALIAS_PARAM);
      window.history.replaceState(window.history.state, "", url.toString());
    }
  };

  // Any row opens the steps dialog, whatever its status; completed workflows get
  // the "Go to Audio Analysis" button inside it.
  const handleRowClick = (row: WorkflowListRow) => {
    setProcessingStartMessage("");
    setAudioUrlParam(row.fileUrl);
  };

  const handleOpenUpload = () => {
    controller.handleFilesChange([]);
    controller.setShowUploadModal(true);
  };

  const handleFormCancel = () => {
    controller.handleFilesChange([]);
    controller.setShowUploadModal(false);
  };

  const handleStepsClose = () => {
    clearAudioUrlParam();
    setProcessingStartMessage("");
    // Refresh once so the list reflects the tracked workflow's latest status.
    controller.fetchWorkflows();
  };

  // Close the multi-upload job dialog and clear the job context.
  const handleJobClose = () => {
    controller.stopJobPolling();
    controller.setFileUploadUUID("");
    controller.setIsProcessCompleted(false);
    controller.setProcessingLogs([]);
    controller.setCompletedPdfName("");
    controller.setCompletedPdfNames([]);
    controller.handleFilesChange([]);
    controller.setShowUploadModal(false);
    controller.fetchWorkflows();
  };

  const handleDialogHide = () => {
    if (dialogMode === "steps") handleStepsClose();
    else if (dialogMode === "job") handleJobClose();
    else handleFormCancel();
  };

  const handleViewJobResults = () => {
    const pdfNames =
      state.data.completedPdfNames.length > 0
        ? state.data.completedPdfNames
        : [state.data.completedPdfName];
    const pdfParam = pdfNames.join(",");
    navigate(
      linkAuthRoute + `/rebate-analysis?pdf=${pdfParam}&job_id=${state.data.fileUploadUUID}`
    );
  };

  const handleAddFiles = (incoming: FileList | File[]) => {
    const files = Array.from(incoming);

    // Filter to only audio files
    const audioFiles = files.filter((file) => {
      const isAudioMime = file.type?.startsWith("audio/");
      const isAudioExt = /\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(file.name);
      return isAudioMime || isAudioExt;
    });

    if (audioFiles.length === 0) {
      toastMessageRef.current?.showFunction?.({
        message: "Invalid file type. Please upload Audio files only.",
        severity: "error",
      });
      return;
    }

    if (audioFiles.length < files.length) {
      toastMessageRef.current?.showFunction?.({
        message: `Only ${audioFiles.length} of ${files.length} files are audio. Non-audio files were ignored.`,
        severity: "info",
      });
    }

    // Append new files to existing selection (avoid duplicates by name)
    const existingFiles = state.data.selectedFiles;
    const existingNames = new Set(existingFiles.map((f) => f.name));
    const newFiles = audioFiles.filter((file) => !existingNames.has(file.name));

    if (newFiles.length === 0) {
      toastMessageRef.current?.showFunction?.({
        message: "All selected files are already in the list.",
        severity: "info",
      });
      return;
    }

    if (newFiles.length < audioFiles.length) {
      toastMessageRef.current?.showFunction?.({
        message: `${audioFiles.length - newFiles.length} file(s) already selected. Added ${newFiles.length} new file(s).`,
        severity: "info",
      });
    }

    controller.handleFilesChange([...existingFiles, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    controller.handleFilesChange(state.data.selectedFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    const filesToUpload = state.data.selectedFiles;

    if (filesToUpload.length === 0) {
      toastMessageRef.current?.showFunction?.({
        message: "No files selected. Add audio from your device.",
        severity: "error",
      });
      return;
    }

    try {
      controller.setProcessingLogs([]);
      setProcessingStartMessage("");

      // A single local audio file triggers the analysis workflow via the upload
      // endpoint; the dialog then flips to the steps view tracking `/audio/progress`.
      if (filesToUpload.length === 1) {
        const response = await PageUploadAudioAPIService.uploadAudio(filesToUpload[0], dispatch);
        const data: UploadAudioResponseData | undefined = response?.data;
        const uploadedUrl = data?.url;
        if (data?.workflowTriggered) {
          setProcessingStartMessage(data?.message || "Audio uploaded; processing started.");
        }
        toastMessageRef.current?.showFunction?.({
          message: data?.message || "Audio uploaded successfully.",
          severity: "success",
        });
        if (uploadedUrl) {
          controller.handleFilesChange([]);
          controller.setShowUploadModal(false);
          setAudioUrlParam(uploadedUrl);
        }
        // Without a url we cannot track the workflow: stay on the form and
        // rely on the toast above.
        return;
      }

      // Multiple files go through the job-based flow; the dialog shows its logs.
      const response = await controller.handleUploadAndAnalyzeMultiple(filesToUpload);
      if (response?.data) {
        toastMessageRef.current?.showFunction?.({
          message: `${filesToUpload.length} file(s) uploaded. Processing and analyzing...`,
          severity: "success",
        });
        controller.handleFilesChange([]);
        controller.setShowUploadModal(false);
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      const errorMessage =
        error?.response?.data?.detail || error?.message || "Error uploading files";
      toastMessageRef.current?.showFunction?.({
        message: `Error: ${errorMessage}`,
        severity: "error",
      });
    }
  };

  const pagination = state.data.audioWorkflowsPagination;

  const dialogTitle =
    dialogMode === "form"
      ? "Upload Audio"
      : dialogMode === "job"
        ? "Processing Audio Files"
        : "Workflow Progress";

  return (
    <div
      className="relative w-full overflow-auto p-3"
      style={{ height: "calc(100vh - 56px)", background: "var(--color-surface-secondary)" }}
    >
      <div
        className="mx-auto flex h-full w-full flex-col rounded-xl p-6"
        style={{
          background: "var(--color-surface-primary)",
          border: "1px solid var(--color-border-weak)",
        }}
      >
        {/* Header: upload action */}
        <div className="mb-4 flex items-center justify-end">
          <ButtonBox
            label="Upload Audio"
            variant="primary"
            size="small"
            onClick={handleOpenUpload}
            className="!bg-purple-600 hover:!bg-purple-700"
          />
        </div>

        {/* Filter tabs */}
        <div className="mb-4 flex gap-1.5">
          {FILTERS.map((item) => {
            const active = filter === item.key;
            return (
              <button
                key={item.key}
                onClick={() => setFilter(item.key)}
                className="rounded-md px-2.5 py-1 text-xs font-medium transition-colors"
                style={{
                  background: active ? "#9333ea" : "var(--color-surface-secondary)",
                  color: active ? "#ffffff" : "var(--color-text-secondary)",
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Error */}
        {state.data.audioWorkflowsError && (
          <div
            className="mb-4 flex items-center justify-between rounded-md px-4 py-3 text-sm"
            style={{
              background: "var(--color-surface-error-weak)",
              color: "var(--color-text-error)",
            }}
          >
            <span>{state.data.audioWorkflowsError}</span>
            <ButtonBox
              label="Retry"
              variant="secondary"
              size="small"
              onClick={() => controller.fetchWorkflows()}
            />
          </div>
        )}

        {/* Workflows table */}
        <div className="min-h-0 flex-1 overflow-auto">
          <WorkflowsListTable
            rows={filteredRows}
            loading={state.data.audioWorkflowsLoading}
            onRowClick={handleRowClick}
          />

          {/* Pagination */}
          {pagination.totalCount > pagination.pageSize && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <ButtonBox
                label="Previous"
                variant="secondary"
                size="small"
                disabled={pagination.page <= 1}
                onClick={() => controller.handlePageChange(Math.max(1, pagination.page - 1))}
              />
              <span className="px-2 text-xs" style={{ color: "var(--color-text-secondary)" }}>
                Page {pagination.page}
              </span>
              <ButtonBox
                label="Next"
                variant="secondary"
                size="small"
                disabled={!pagination.hasNext}
                onClick={() => controller.handlePageChange(pagination.page + 1)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Upload / steps / job dialog */}
      {/* PrimeReact renders an (empty, transparent) header by default, which leaves the
          white content box with square top corners; the dialog title is our own h2. */}
      <DialogBox visible={dialogMode !== "closed"} onHide={handleDialogHide} showHeader={false}>
        <div className="flex flex-col px-6 py-6" style={{ minWidth: 460, maxWidth: 560 }}>
          <h2
            className="text-base font-semibold"
            style={{ color: "var(--color-text-primary)", marginBottom: "8px" }}
          >
            {dialogTitle}
          </h2>

          {dialogMode === "form" && (
            <UploadFormView
              selectedFiles={state.data.selectedFiles}
              isUploading={state.data.isUploading}
              onAddFiles={handleAddFiles}
              onRemoveFile={handleRemoveFile}
              onCancel={handleFormCancel}
              onUpload={handleUpload}
            />
          )}

          {/* Conditionally mounted so the 3s `/audio/progress` poll starts and
              stops with the dialog. */}
          {dialogMode === "steps" && (
            <WorkflowStepsView
              audioUrl={audioUrlParam}
              processingStartMessage={processingStartMessage}
              onClose={handleStepsClose}
            />
          )}

          {dialogMode === "job" && (
            <>
              {!state.data.isProcessCompleted ? (
                <div className="mb-3 flex items-center gap-2 rounded-lg bg-blue-50 p-3">
                  <i className="pi pi-spin pi-spinner text-blue-700" />
                  <span className="text-sm font-semibold text-blue-700">
                    Processing audio files and analyzing...
                  </span>
                </div>
              ) : (
                (state.data.completedPdfNames.length > 0 || state.data.completedPdfName) && (
                  <div className="mb-3 flex items-center gap-2 rounded-lg bg-green-50 p-3">
                    <i className="pi pi-check-circle text-green-600" />
                    <span className="text-sm font-semibold text-green-700">
                      Processing and analysis completed successfully!
                      {state.data.completedPdfNames.length > 1 &&
                        ` (${state.data.completedPdfNames.length} audio files)`}
                    </span>
                  </div>
                )
              )}

              {/* Processing logs */}
              <div className="flex min-h-0 flex-col rounded-lg border border-gray-200 bg-gray-50">
                <div className="shrink-0 border-b bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700">
                  Processing Logs
                </div>
                <div className="max-h-[min(40vh,360px)] min-h-[80px] flex-1 overflow-y-auto overscroll-contain p-3">
                  {sortedProcessingLogs.length === 0 ? (
                    <span className="text-xs text-gray-500">Waiting for logs…</span>
                  ) : (
                    sortedProcessingLogs.map((log, idx) => {
                      const getLogColor = (level: string) => {
                        switch (level) {
                          case "success":
                            return "text-green-700";
                          case "error":
                            return "text-red-700";
                          case "warning":
                            return "text-yellow-700";
                          default:
                            return "text-gray-700";
                        }
                      };

                      const getLogIcon = (level: string) => {
                        switch (level) {
                          case "success":
                            return "✓";
                          case "error":
                            return "✗";
                          case "warning":
                            return "⚠";
                          default:
                            return "•";
                        }
                      };

                      return (
                        <div
                          key={`${log.timestamp}-${idx}-${log.message.slice(0, 24)}`}
                          className="flex items-start gap-2 border-b border-gray-100 py-2 text-sm last:border-b-0 last:pb-0"
                        >
                          <span className={`font-semibold ${getLogColor(log.level)}`}>
                            {getLogIcon(log.level)}
                          </span>
                          <span className={`flex-1 ${getLogColor(log.level)}`}>{log.message}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-1.5">
                <ButtonBox
                  label={state.data.isProcessCompleted ? "Close" : "Run in background"}
                  variant="secondary"
                  size="small"
                  onClick={handleJobClose}
                />
                {state.data.isProcessCompleted &&
                  (state.data.completedPdfNames.length > 0 || state.data.completedPdfName) && (
                    <ButtonBox
                      label="View Analysis Results"
                      variant="primary"
                      size="small"
                      onClick={handleViewJobResults}
                      className="!bg-purple-600 hover:!bg-purple-700"
                    />
                  )}
              </div>
            </>
          )}
        </div>
      </DialogBox>

      <ToastCustom ref={toastMessageRef} position="bottom-right" />
    </div>
  );
};

export default PageUploadAudio;
